import { features } from 'constants/featureFlags';
import TrackingEvent from 'constants/trackingEvents';
import { useTranslation } from 'react-i18next';
import { Channel, ChannelFilters, StreamChat } from 'stream-chat';

import React, { ReactNode, createContext, useContext, useState } from 'react';

import { toaster } from '@thinkific/toga-react';

import { useCommunityContext } from 'components/CommunityContext';

import StreamSessionProvider from './StreamSessionProvider';

interface ChatProviderProps {
  children: ReactNode;
}

interface ChatContextValue {
  conversations: Channel[];
  isDirectMessagingEnabled: boolean;
  isNewChannelModalOpen: boolean;
  toggleNewConversationModal: () => void;
  session: StreamChat | null;
  currentConversation: Channel | null;
  setActiveConversation: (conversation: Channel) => void;
  hasUnreadMessages: boolean;
  findExistingConversation: (userIds: string[]) => Promise<Channel | null>;
  baseChannelFilters: ChannelFilters;
}

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

export const useChatContext = (): ChatContextValue => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }: ChatProviderProps) => {
  const { site, community, trackEvent, featureFlags } = useCommunityContext();
  const { t } = useTranslation();
  const [isNewChannelModalOpen, setIsNewChannelModalOpen] = useState<boolean>(false);
  const { isEnabled: directMessagingInPlan } = site?.features?.directMessaging || {};

  const directMessagingEnabled = community?.directMessagingEnabled;

  const isDirectMessagingEnabled =
    featureFlags[features.DIRECT_MESSAGING] && directMessagingInPlan && directMessagingEnabled;

  const toggleNewConversationModal = () => {
    if (!directMessagingInPlan) {
      toaster.notice(
        t(
          'pages-direct-messages_cannot-start-conversation',
          'Direct messaging is not available on your plan.'
        )
      );
      return;
    }
    if (!directMessagingEnabled) {
      toaster.notice(
        t(
          'pages-direct-messages_cannot-start-conversation',
          'Please turn on direct messaging in the community settings.'
        )
      );
      return;
    }
    trackEvent(TrackingEvent.DIRECT_MESSAGES_START_CONVERSATION_CLICKED);
    setIsNewChannelModalOpen(!isNewChannelModalOpen);
  };

  const contextValues = {
    isDirectMessagingEnabled,
    conversations: [],
    isNewChannelModalOpen,
    toggleNewConversationModal,
    session: null,
    setActiveConversation: () => {},
    currentConversation: null,
    hasUnreadMessages: false,
    findExistingConversation: () => Promise.resolve(null),
    baseChannelFilters: {},
  };

  if (!isDirectMessagingEnabled) {
    return <ChatContext.Provider value={contextValues}>{children}</ChatContext.Provider>;
  }

  return (
    <StreamSessionProvider>
      {({
        session,
        setActiveConversation,
        currentConversation,
        hasUnreadMessages,
        findExistingConversation,
        baseChannelFilters,
        conversations,
      }) => {
        return (
          <ChatContext.Provider
            value={{
              ...contextValues,
              conversations,
              session,
              setActiveConversation,
              currentConversation,
              hasUnreadMessages,
              findExistingConversation,
              baseChannelFilters,
            }}
          >
            {children}
          </ChatContext.Provider>
        );
      }}
    </StreamSessionProvider>
  );
};
